import { useRef, useState, useCallback, useEffect } from "react";
import { Empty, Typography, Select, Button, Modal, Space } from "antd";
import { PlusOutlined, StopOutlined } from "@ant-design/icons";
import { SensorPin } from "./SensorPin";

const { Text } = Typography;

export const DiagramCanvas = ({
  diagramUrl,
  pins,
  deviceMetrics,
  devices,
  isEditMode,
  onAddPin,
  onRemovePin,
  onUpdatePinPosition,
}) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const [isPlacingMode, setIsPlacingMode] = useState(false);
  const [selectedDeviceToPlace, setSelectedDeviceToPlace] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pendingPos, setPendingPos] = useState(null);
  const [livePins, setLivePins] = useState({});

  // Reset placing mode when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) setIsPlacingMode(false);
  }, [isEditMode]);

  const pinnedSet = new Set(pins.map((p) => p.devEui));
  const available = devices.filter((d) => !pinnedSet.has(d.devEui));

  const handleCanvasClick = useCallback(
    (e) => {
      if (!isEditMode || !isPlacingMode) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setPendingPos({ x, y });
      setSelectedDeviceToPlace(null);
      setShowModal(true);
    },
    [isEditMode, isPlacingMode],
  );

  const handleConfirm = useCallback(() => {
    if (selectedDeviceToPlace && pendingPos) {
      onAddPin(selectedDeviceToPlace, pendingPos.x, pendingPos.y);
    }
    setShowModal(false);
    setIsPlacingMode(false);
    setSelectedDeviceToPlace(null);
    setPendingPos(null);
  }, [selectedDeviceToPlace, pendingPos, onAddPin]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setPendingPos(null);
    setSelectedDeviceToPlace(null);
  }, []);

  // Drag with rAF throttling to cap re-renders at ~60fps (rerender-use-ref-transient-values)
  const handleDragStart = useCallback(
    (e, pinId, onMovedCb) => {
      e.preventDefault();
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const pin = pins.find((p) => p.id === pinId);
      if (!pin) return;

      const startCX = e.clientX;
      const startCY = e.clientY;
      let moved = false;
      let latestX = pin.x;
      let latestY = pin.y;

      const onMove = (e) => {
        const dx = ((e.clientX - startCX) / rect.width) * 100;
        const dy = ((e.clientY - startCY) / rect.height) * 100;
        if (!moved && (Math.abs(dx) > 0.4 || Math.abs(dy) > 0.4)) {
          moved = true;
          onMovedCb?.();
        }
        latestX = Math.max(2, Math.min(98, pin.x + dx));
        latestY = Math.max(2, Math.min(98, pin.y + dy));

        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            setLivePins((prev) => ({ ...prev, [pinId]: { x: latestX, y: latestY } }));
            rafRef.current = null;
          });
        }
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        if (moved) onUpdatePinPosition(pinId, latestX, latestY);
        setLivePins((prev) => { const n = { ...prev }; delete n[pinId]; return n; });
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [pins, onUpdatePinPosition],
  );

  const resolvedPin = (pin) => {
    const live = livePins[pin.id];
    return live ? { ...pin, ...live } : pin;
  };

  // Device options with location context for disambiguation
  const deviceOptions = available.map((d) => ({
    value: d.devEui,
    label: d.name,
    location: d.location?.name || d.storeName || "",
  }));

  const canvasClass = ["unifilar-canvas", diagramUrl ? "unifilar-canvas--has-diagram" : ""].join(" ");

  return (
    <>
      {/* Toolbar — only shown in edit mode */}
      {isEditMode && (
        <div className="canvas-toolbar">
          {!isPlacingMode ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsPlacingMode(true)}
              disabled={available.length === 0}
              style={{ borderRadius: 6 }}
            >
              Agregar sensor
            </Button>
          ) : (
            <>
              <Button
                icon={<StopOutlined />}
                onClick={() => setIsPlacingMode(false)}
                style={{ borderRadius: 6 }}
              >
                Cancelar
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Haz clic en el diagrama para ubicar el sensor
              </Text>
            </>
          )}

          {available.length === 0 && devices.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
              Todos los sensores activos ya están colocados
            </Text>
          )}

          {devices.length === 0 && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
              Cargando dispositivos...
            </Text>
          )}
        </div>
      )}

      {/* Canvas */}
      <div
        ref={canvasRef}
        className={canvasClass}
        onClick={handleCanvasClick}
        style={{
          position: "relative",
          width: "100%",
          minHeight: 480,
          borderRadius: isEditMode ? "0 0 8px 8px" : 8,
          border: isPlacingMode
            ? "2px dashed #3B82F6"
            : "1px solid rgba(0,0,0,0.1)",
          overflow: "hidden",
          cursor: isPlacingMode ? "crosshair" : "default",
          userSelect: "none",
        }}
      >
        {diagramUrl ? (
          <img
            src={diagramUrl}
            alt="Diagrama unifilar"
            draggable={false}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              pointerEvents: "none",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 480,
            }}
          >
            <Empty
              description={
                <Space direction="vertical" size={4} style={{ textAlign: "center" }}>
                  <Text type="secondary">Sin diagrama cargado</Text>
                  {isEditMode && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Usa el botón "Cargar diagrama" en el panel lateral
                    </Text>
                  )}
                </Space>
              }
            />
          </div>
        )}

        {/* Sensor pins */}
        {pins.map((pin) => (
          <SensorPin
            key={pin.id}
            pin={resolvedPin(pin)}
            metrics={deviceMetrics[pin.devEui]}
            isEditMode={isEditMode}
            onRemove={onRemovePin}
            onDragStart={handleDragStart}
          />
        ))}

        {isPlacingMode && (
          <div className="placing-hint">
            Clic en el punto del diagrama donde quieres el sensor
          </div>
        )}
      </div>

      {/* Device selection modal */}
      <Modal
        title="Seleccionar sensor"
        open={showModal}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Colocar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !selectedDeviceToPlace }}
        width={420}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 14, fontSize: 13 }}>
          Elige el dispositivo que quieres ubicar en esta posición del diagrama.
        </Text>
        <Select
          placeholder="Buscar dispositivo..."
          style={{ width: "100%" }}
          value={selectedDeviceToPlace}
          onChange={setSelectedDeviceToPlace}
          options={deviceOptions}
          optionRender={(option) => (
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{option.data.label}</div>
              {option.data.location && (
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{option.data.location}</div>
              )}
            </div>
          )}
          showSearch
          filterOption={(input, opt) =>
            (opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          size="large"
          autoFocus
        />
      </Modal>
    </>
  );
};
